// DashboardTransportPro.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../composant_reutilisable/sidebar';
import Navbar from '../composant_reutilisable/navbar';
import { Link } from 'react-router-dom';
import { FaHome, FaSpinner, FaSync } from 'react-icons/fa';

const Dashboard = ({ user_id }) => {
    // État pour les données
    const [data, setData] = useState({
        total_vehicules: 0,
        vehicules_entres: 0,
        vehicules_selectionnes: 0,
        lettres_voiture: 0,
        fret_declares: 0,
        total_parcs: 0,
        parcs: [],
        types_vehicules: [],
        categories_fret: [],
        // Statistiques globales supplémentaires
        total_sorties: 0,
        total_fret: 0,
        total_factures: 0
    });

    // États pour le chargement
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(new Date());

    // États pour les données de référence
    const [parcs, setParcs] = useState([]);
    const [typesVehicule, setTypesVehicule] = useState([]);
    const [categories, setCategories] = useState([]);
    const [allStats, setAllStats] = useState({
        entrees: null,
        sorties: null,
        lettre_dispo: null,
        fret: null,
        fret_termine: null,
        organisation: null,
        factures: null,
        entreesListe: null,
        sortiesListe: null,
        lettre_dispoListe: null,
        fretListe: null,
        fret_termineListe: null,
        organisationListe: null,
        facturesListe: null,
    });

    // Chargement des données de référence
    useEffect(() => {
        const fetchReferenceData = async () => {
            try {
                const parcsResponse = await axios.get(process.env.REACT_APP_HOTE + "/liste-parc/");
                setParcs(parcsResponse.data);

                const typesResponse = await axios.get(process.env.REACT_APP_HOTE + "/liste-types/");
                setTypesVehicule(typesResponse.data);

                const listeCategorie = await axios.get(process.env.REACT_APP_HOTE + "/liste-categories/");
                setCategories(listeCategorie.data);

                // Après avoir chargé les références, charger les stats
                await fetchAllStats();
            } catch (error) {
                console.error("Erreur lors du chargement des données de référence:", error);
                setError("Erreur de chargement des données");
                setLoading(false);
            }
        };
        fetchReferenceData();
    }, []);

    // Fonction pour récupérer toutes les statistiques
    const fetchAllStats = async (showRefresh = false) => {
        try {
            if (showRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);

            const typesStat = ['entrees', 'sorties', 'lettre_dispo', 'fret', 'fret_termine', 'organisation', 'factures'];
            const results = {};

            // Récupérer chaque type de statistique
            for (const type of typesStat) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_HOTE}/statistiques-transport/`,
                        {
                            params: {
                                type_stat: type,
                                periode: 'Tout'
                            }
                        }
                    );
                    if (response.data.success) {
                        results[type] = {
                            resultats_agreges: response.data.resultats_agreges || {},
                            elements_complets: response.data.elements_complets || []
                        };
                    }
                } catch (err) {
                    console.error(`Erreur pour ${type}:`, err);
                }
            }

            // Mettre à jour allStats
            setAllStats({
                entrees: results.entrees?.resultats_agreges || null,
                sorties: results.sorties?.resultats_agreges || null,
                lettre_dispo: results.lettre_dispo?.resultats_agreges || null,
                fret: results.fret?.resultats_agreges || null,
                fret_termine: results.fret_termine?.resultats_agreges || null,
                organisation: results.organisation?.resultats_agreges || null,
                factures: results.factures?.resultats_agreges || null,
                entreesListe: results.entrees?.elements_complets || [],
                sortiesListe: results.sorties?.elements_complets || [],
                lettre_dispoListe: results.lettre_dispo?.elements_complets || [],
                fretListe: results.fret?.elements_complets || [],
                fret_termineListe: results.fret_termine?.elements_complets || [],
                organisationListe: results.organisation?.elements_complets || [],
                facturesListe: results.factures?.elements_complets || [],
            });

            // Traiter les données pour le tableau de bord
            processDashboardData(results);

        } catch (err) {
            console.error("❌ Erreur de chargement:", err);
            setError("Erreur lors du chargement des statistiques");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Traitement des données pour le dashboard
    const processDashboardData = (results) => {
        try {
            // 1. Récupérer toutes les listes
            const toutesListes = [
                ...(results.entrees?.elements_complets || []),
                ...(results.sorties?.elements_complets || []),
                ...(results.lettre_dispo?.elements_complets || [])
            ];

            // 2. Statistiques globales
            const totalVehicules = toutesListes.length;
            const vehiculesEntres = results.entrees?.elements_complets?.length || 0;
            const vehiculesSortis = results.sorties?.elements_complets?.length || 0;
            const lettresVoiture = results.lettre_dispo?.elements_complets?.length || 0;
            const totalFret = results.fret?.elements_complets?.length || 0;
            const totalFactures = results.factures?.elements_complets?.length || 0;

            // 3. Nombre de parcs UNIQUES
            const parcSet = new Set();
            toutesListes.forEach(item => {
                if (item.parc) {
                    parcSet.add(item.parc);
                }
            });
            const totalParcs = parcSet.size;

            // 4. Statistiques par parc
            const parcsStats = {};
            toutesListes.forEach(item => {
                const parcRef = item.parc || 'Non spécifié';
                const parcNom = parcs.find(p => p.ref_parc === parcRef)?.nom_parc || parcRef;
                
                if (!parcsStats[parcRef]) {
                    parcsStats[parcRef] = {
                        id: parcs.find(p => p.ref_parc === parcRef)?.id || 0,
                        nom: parcNom,
                        ref: parcRef,
                        total_vehicules: 0,
                        vehicules_entres: 0,
                        vehicules_sortis: 0,
                        lettres: 0,
                        frets: 0
                    };
                }
                parcsStats[parcRef].total_vehicules++;
                
                // Compter par statut
                if (item.statut === 'Selectionne') {
                    parcsStats[parcRef].vehicules_entres++;
                }
                if (item.statut === 'Sorti' || item.statut === 'Quitter') {
                    parcsStats[parcRef].vehicules_sortis++;
                }
                if (item.statut === 'lettre_dispo') {
                    parcsStats[parcRef].lettres++;
                }
            });

            // Ajouter les frets par parc
            (results.fret?.elements_complets || []).forEach(item => {
                const parcRef = item.parc_ref || item.parc || 'Non spécifié';
                if (parcsStats[parcRef]) {
                    parcsStats[parcRef].frets = (parcsStats[parcRef].frets || 0) + 1;
                }
            });

            const parcsArray = Object.values(parcsStats);

            // 5. Types de véhicules
            const typesStats = {};
            toutesListes.forEach(item => {
                const typeNom = item.type_camion || 'Non spécifié';
                if (!typesStats[typeNom]) {
                    typesStats[typeNom] = { nom: typeNom, count: 0 };
                }
                typesStats[typeNom].count++;
            });
            const typesArray = Object.values(typesStats).sort((a, b) => b.count - a.count);

            // 6. Catégories de fret
            const categoriesFret = results.fret?.resultats_agreges?.par_categorie || [];
            const categoriesArray = categoriesFret.map(cat => ({
                nom: cat.cle || cat.nom || 'Non spécifié',
                count: cat.nombre || cat.count || 0
            }));

            // 7. Mise à jour du state
            setData({
                total_vehicules: totalVehicules,
                vehicules_entres: vehiculesEntres,
                vehicules_selectionnes: vehiculesEntres, // Même chose que entrés pour l'instant
                lettres_voiture: lettresVoiture,
                fret_declares: totalFret,
                total_parcs: totalParcs,
                total_sorties: vehiculesSortis,
                total_fret: totalFret,
                total_factures: totalFactures,
                parcs: parcsArray,
                types_vehicules: typesArray,
                categories_fret: categoriesArray
            });

            setLastUpdate(new Date());

        } catch (err) {
            console.error("Erreur traitement données:", err);
        }
    };

    const handleRefresh = () => {
        fetchAllStats(true);
    };

    if (loading) {
        return (
            <div id="wrapper">
                <Sidebar user_id={user_id} />
                <div id="page-wrapper" className="gray-bg">
                    <Navbar />
                    <div className="wrapper wrapper-content animated fadeInRight">
                        <div className="text-center m-t-lg p-lg">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Chargement...</span>
                            </div>
                            <h4 className="m-t-md">Chargement des statistiques...</h4>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div id="wrapper">
                <Sidebar user_id={user_id} />
                <div id="page-wrapper" className="gray-bg">
                    <Navbar />
                    <div className="wrapper wrapper-content animated fadeInRight">
                        <div className="alert alert-danger">
                            <i className="fa fa-exclamation-triangle m-r-sm"></i>
                            <strong>Erreur :</strong> {error}
                        </div>
                        <button className="btn btn-primary" onClick={handleRefresh}>
                            <i className="fa fa-refresh m-r-xs"></i> Réessayer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="wrapper">
            <Sidebar user_id={user_id} />
            <div id="page-wrapper" className="gray-bg">
                <Navbar />
                <div>
                    <div className="row wrapper border-bottom white-bg page-heading">
                        <div className="col-lg-12">
                            <h2 className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <FaHome size={25} className='mr-2' />Tableau de bord
                                </div>
                            </h2>
                            <small>Tableau de bord affichant les données statistiques.</small>

                            <small id='hbreadcrumb' className='d-flex justify-content-end'>
                                <ol className="breadcrumb custom-breadcrumb">
                                    <li className="breadcrumb-item custom-breadcrumb-item">
                                        <Link to={`/Menu`}> Accueil</Link>
                                    </li>
                                    <li className="breadcrumb-item custom-breadcrumb-item active">
                                        Tableau de bord
                                    </li>
                                </ol>
                            </small>
                        </div>
                    </div>

                    <div className="wrapper wrapper-content animated fadeInRight">
                        {/* En-tête */}
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="ibox float-e-margins">
                                    <div className="ibox-title">
                                        <h5>
                                            <i className="fa fa-tachometer m-r-sm"></i>
                                            TransportPro - Tableau de bord des statistiques
                                        </h5>
                                        <div className="ibox-tools">
                                            <span className="text-muted small m-r-sm">
                                                <i className="fa fa-calendar"></i> Mise à jour : {lastUpdate.toLocaleString('fr-FR')}
                                            </span>
                                            <button
                                                className="btn btn-white btn-xs"
                                                onClick={handleRefresh}
                                                disabled={refreshing}
                                            >
                                                {refreshing ? (
                                                    <>
                                                        <FaSpinner className="fa-spin m-r-xs" />
                                                        Actualisation...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaSync className="m-r-xs" />
                                                        Actualiser
                                                    </>
                                                )}
                                            </button>
                                            {/* <button className="btn btn-white btn-xs" onClick={() => window.location.reload()}>
                                                <i className="fa fa-refresh"></i>
                                            </button> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 1: Statistiques globales */}
                        <div className="row" style={{ color: 'white' }}>
                            <div className="col-lg-3">
                                <div className="ibox">
                                    <div className="ibox-content" style={{ backgroundColor: '#1c84c6' }}>
                                        <h5>Total véhicules</h5>
                                        <h1 className="no-margins bold">{data.total_vehicules.toLocaleString('fr-FR')}</h1>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="ibox">
                                    <div className="ibox-content" style={{ backgroundColor: '#23c6c8' }}>
                                        <h5>Véhicules entrés</h5>
                                        <h1 className="no-margins bold">{data.vehicules_entres.toLocaleString('fr-FR')}</h1>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="ibox">
                                    <div className="ibox-content" style={{ backgroundColor: '#f8ac59' }}>
                                        <h5>Véhicules sortis</h5>
                                        <h1 className="no-margins bold">{data.total_sorties.toLocaleString('fr-FR')}</h1>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="ibox">
                                    <div className="ibox-content" style={{ backgroundColor: '#ed5565' }}>
                                        <h5>Lettres de voiture</h5>
                                        <h1 className="no-margins bold">{data.lettres_voiture.toLocaleString('fr-FR')}</h1>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="ibox">
                                    <div className="ibox-content" style={{ backgroundColor: '#8870c6' }}>
                                        <h5>Frets déclarés</h5>
                                        <h1 className="no-margins bold">{data.fret_declares.toLocaleString('fr-FR')}</h1>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="ibox">
                                    <div className="ibox-content" style={{ backgroundColor: '#ff6b6b' }}>
                                        <h5>Parcs</h5>
                                        <h1 className="no-margins bold">{data.total_parcs.toLocaleString('fr-FR')}</h1>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Statistiques des types de véhicules et catégorie de fret */}
                        <div className="row">
                            {/* Par type de véhicules */}
                            <div className="col-lg-6">
                                <div className="ibox">
                                    <div className="ibox-title">
                                        <h5>
                                            <i className="fa fa-truck m-r-sm"></i>
                                            Par type de véhicule
                                        </h5>
                                        <div className="ibox-tools">
                                            <span className="badge badge-primary">{data.types_vehicules.length} types</span>
                                        </div>
                                    </div>
                                    <div className="ibox-content">
                                        <div>
                                            <div className="chat-activity-list">
                                                {data.types_vehicules.slice(0, 10).map((type, index) => {
                                                    const maxCount = data.types_vehicules.length > 0 ? data.types_vehicules[0].count : 1;
                                                    const percentage = Math.round((type.count / maxCount) * 100);
                                                    return (
                                                        <div className="chat-element" key={index}>
                                                            <div className="media-body">
                                                                <div className="row">
                                                                    <div className="col-lg-8">
                                                                        {type.nom}
                                                                    </div>
                                                                    <div className="col-lg-4 text-right">
                                                                        <strong>{type.count}</strong>
                                                                    </div>
                                                                </div>
                                                                {/* <div className="progress progress-mini m-t-xs">
                                                                    <div 
                                                                        className="progress-bar progress-bar-primary"
                                                                        style={{ width: `${percentage}%` }}
                                                                    />
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {data.types_vehicules.length === 0 && (
                                                    <div className="text-center text-muted p-md">
                                                        <i className="fa fa-info-circle m-r-xs"></i>
                                                        Aucun type de véhicule disponible
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Par catégorie de fret */}
                            <div className="col-lg-6">
                                <div className="ibox">
                                    <div className="ibox-title">
                                        <h5>
                                            <i className="fa fa-boxes m-r-sm"></i>
                                            Par catégorie de fret
                                        </h5>
                                        <div className="ibox-tools">
                                            <span className="badge badge-primary">{data.categories_fret.length} catégories</span>
                                        </div>
                                    </div>
                                    <div className="ibox-content">
                                        <div>
                                            <div className="chat-activity-list">
                                                {data.categories_fret.slice(0, 10).map((categorie, index) => {
                                                    const maxCount = data.categories_fret.length > 0 ? data.categories_fret[0].count : 1;
                                                    const percentage = Math.round((categorie.count / maxCount) * 100);
                                                    return (
                                                        <div className="chat-element" key={index}>
                                                            <div className="media-body">
                                                                <div className="row">
                                                                    <div className="col-lg-8">
                                                                        {categorie.nom}
                                                                    </div>
                                                                    <div className="col-lg-4 text-right">
                                                                        <strong>{categorie.count}</strong>
                                                                    </div>
                                                                </div>
                                                                {/* <div className="progress progress-mini m-t-xs">
                                                                    <div 
                                                                        className="progress-bar progress-bar-success"
                                                                        style={{ width: `${percentage}%` }}
                                                                    />
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {data.categories_fret.length === 0 && (
                                                    <div className="text-center text-muted p-md">
                                                        <i className="fa fa-info-circle m-r-xs"></i>
                                                        Aucune catégorie de fret disponible
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Détail par Parc */}
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="ibox float-e-margins">
                                    <div className="ibox-title">
                                        <h5>
                                            <i className="fa fa-table m-r-sm"></i>
                                            Détail des Statistiques par Parc
                                        </h5>
                                        <div className="ibox-tools">
                                            <span className="badge badge-primary">{data.parcs.length} parcs</span>
                                        </div>
                                    </div>
                                    <div className="ibox-content">
                                        {data.parcs.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-striped table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Parc</th>
                                                            <th>Référence</th>
                                                            <th className="text-center">Total Véhicules</th>
                                                            <th className="text-center">Entrées</th>
                                                            <th className="text-center">Sorties</th>
                                                            <th className="text-center">Lettres</th>
                                                            <th className="text-center">Frets</th>
                                                            <th className="text-center">Taux d'occupation</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {data.parcs.map((parc, index) => {
                                                            const tauxOccupation = parc.total_vehicules > 0
                                                                ? Math.round((parc.vehicules_entres / parc.total_vehicules) * 100)
                                                                : 0;
                                                            return (
                                                                <tr key={index}>
                                                                    <td>{index + 1}</td>
                                                                    <td><strong>{parc.nom}</strong></td>
                                                                    <td><span className="badge badge-default">{parc.ref}</span></td>
                                                                    <td className="text-center"><strong>{parc.total_vehicules}</strong></td>
                                                                    <td className="text-center">{parc.vehicules_entres}</td>
                                                                    <td className="text-center">{parc.vehicules_sortis}</td>
                                                                    <td className="text-center">{parc.lettres}</td>
                                                                    <td className="text-center">{parc.frets || 0}</td>
                                                                    <td className="text-center">
                                                                        <div className="progress progress-mini m-t-xs">
                                                                            <div
                                                                                className={`progress-bar ${tauxOccupation > 70 ? 'progress-bar-success' : tauxOccupation > 40 ? 'progress-bar-warning' : 'progress-bar-danger'}`}
                                                                                style={{ width: `${tauxOccupation}%` }}
                                                                            />
                                                                        </div>
                                                                        <small className="text-muted">{tauxOccupation}%</small>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="bg-light">
                                                            <td colSpan="2"><strong>Totaux</strong></td>
                                                            <td></td>
                                                            <td className="text-center"><strong>{data.total_vehicules}</strong></td>
                                                            <td className="text-center"><strong>{data.vehicules_entres}</strong></td>
                                                            <td className="text-center"><strong>{data.total_sorties}</strong></td>
                                                            <td className="text-center"><strong>{data.lettres_voiture}</strong></td>
                                                            <td className="text-center"><strong>{data.fret_declares}</strong></td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted p-lg">
                                                <i className="fa fa-info-circle fa-2x m-b-sm"></i>
                                                <p>Aucune donnée de parc disponible</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Pied de page */}
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="text-center text-muted small">
                                    <p>
                                        <i className="fa fa-database m-r-xs"></i>
                                        Données mises à jour en temps réel
                                        <span className="m-l-sm m-r-sm">|</span>
                                        <i className="fa fa-truck m-r-xs"></i>
                                        {data.total_vehicules} véhicules gérés
                                        <span className="m-l-sm m-r-sm">|</span>
                                        <i className="fa fa-building m-r-xs"></i>
                                        {data.total_parcs} parcs actifs
                                        <span className="m-l-sm m-r-sm">|</span>
                                        <i className="fa fa-file-text m-r-xs"></i>
                                        {data.lettres_voiture} lettres émises
                                        <span className="m-l-sm m-r-sm">|</span>
                                        <i className="fa fa-boxes m-r-xs"></i>
                                        {data.fret_declares} frets déclarés
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;